import {
  PersistedFormGroup,
  PersistedFormArray,
  PersistedArray,
} from './persisted-form-group';
import type {
  PersistedFormGroupOptions,
  PersistedFormArrayOptions,
  PersistedArrayOptions,
} from './persisted-form-group';
import type { SchemaFormControl } from '@apexdesigner/schema-forms';
import { StudentAssignment } from './student-assignment';
export { StudentAssignment };
import { studentAssignmentSchema } from '@schemas/business-objects/student-assignment';
import { SelectedStudentFormGroup } from './selected-student-form-group';
import { SlotFormGroup } from './slot-form-group';
import { TermTutorFormGroup } from './term-tutor-form-group';
import type { Id } from '../base-types';
import type { Student } from './student';
import type { Term } from './term';
import type { TermTime } from './term-time';
import type { Tutor } from './tutor';
import type { User } from './user';

export class StudentAssignmentFormGroup extends PersistedFormGroup {
  declare controls: {
    id: SchemaFormControl;
    from: SchemaFormControl;
    to: SchemaFormControl;
    status: SchemaFormControl;
    selectedStudentId: SchemaFormControl;
    selectedStudent: SelectedStudentFormGroup;
    slotId: SchemaFormControl;
    slot: SlotFormGroup;
    termTutorId: SchemaFormControl;
    termTutor: TermTutorFormGroup;
    replacesId: SchemaFormControl;
    replaces: StudentAssignmentFormGroup;
    replacedBy: StudentAssignmentFormGroup;
  };

  declare value: Partial<StudentAssignment>;

  constructor(
    data?: Record<string, any> | null,
    options?: PersistedFormGroupOptions,
  ) {
    super(studentAssignmentSchema, StudentAssignment, data, options);
  }

  get object(): StudentAssignment {
    return new StudentAssignment(this.getRawValue());
  }

  protected override createControl(name: string) {
    switch (name) {
      case 'selectedStudent':
        return new SelectedStudentFormGroup();
      case 'slot':
        return new SlotFormGroup();
      case 'termTutor':
        return new TermTutorFormGroup();
      case 'replaces':
        return new StudentAssignmentFormGroup();
      case 'replacedBy':
        return new StudentAssignmentFormGroup();
      default:
        return undefined;
    }
  }

  static async find(filter?: any): Promise<StudentAssignment[]> {
    return StudentAssignment.find(filter);
  }

  static async findOne(filter?: any): Promise<StudentAssignment | null> {
    return StudentAssignment.findOne(filter);
  }

  static async findById(id: number, filter?: any): Promise<StudentAssignment> {
    return StudentAssignment.findById(id, filter);
  }

  static async create(
    data: Partial<StudentAssignment>,
  ): Promise<StudentAssignment> {
    return StudentAssignment.create(data as any);
  }

  static async updateById(
    id: number,
    updates: Partial<StudentAssignment>,
  ): Promise<StudentAssignment> {
    return StudentAssignment.updateById(id, updates as any);
  }

  static async deleteById(id: number): Promise<boolean> {
    return StudentAssignment.deleteById(id);
  }

  static async checkExistingProposal(
    termTutorId: Id,
    slotId: Id | undefined,
    selectedStudentId: Id,
    replacesId: Id | undefined,
    from?: string,
    to?: string,
  ): Promise<any> {
    return StudentAssignment.checkExistingProposal(
      termTutorId,
      slotId,
      selectedStudentId,
      replacesId,
      from,
      to,
    );
  }
}

export class StudentAssignmentFormArray extends PersistedFormArray {
  readonly entityName = 'StudentAssignment' as const;

  constructor(options?: PersistedFormArrayOptions) {
    super(studentAssignmentSchema, StudentAssignment, options);
  }

  protected override createItemGroup() {
    return new StudentAssignmentFormGroup();
  }

  get array(): StudentAssignment[] {
    return this.controls.map(
      (group: StudentAssignmentFormGroup) => group.object,
    );
  }
}

export class StudentAssignmentPersistedArray extends PersistedArray<StudentAssignment> {
  readonly entityName = 'StudentAssignment' as const;

  constructor(options?: PersistedArrayOptions) {
    super(StudentAssignment, options);
  }
}
